import { Directive, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, Renderer2, AfterViewInit } from '@angular/core';
import { DragSource, SortableService } from 'services/sortable.service';
import { toCanvas } from 'html-to-image';

@Directive({ selector: '[sortable]' })
export class SortableDirective implements AfterViewInit {

	@Input('sortable') items: any[] = [];
	@Input() handleSelector?: string;
	
	@Input() item!: any;
	
	@Output() itemsChange = new EventEmitter<any[]>();
	@Output() sortend = new EventEmitter();
	
	@HostBinding('class.dragging') dragging = false;
	@HostBinding('class.sortable') sortable = false;
	@HostBinding('attr.sortableItem') sortableItem = true;

	private element: Element;
	private handle: Element | null = null;
	private dragSource?: DragSource;
	private dropSource?: Element;
	private globalListenFunc: Function | undefined;

	private ghostElement: HTMLElement | null = null;
	private ghostOffsetX = 0;
	private ghostOffsetY = 0;

	private bodyFunc?: ((this: GlobalEventHandlers, ev: MouseEvent) => any);

	ngAfterViewInit(): void {
		this.handle = this.handleSelector
			? this.element!.querySelector(this.handleSelector)
			: this.element;
		if (this.handleSelector)
			this.handle?.classList.add('sort-handle');
	}

	constructor(element: ElementRef<HTMLElement>, private sortableService: SortableService, private renderer: Renderer2) {
		this.element = element.nativeElement;
		this.sortableService.dragSource$.subscribe(dragSource => {
			this.dragSource = dragSource;
		});
		this.sortableService.dropSource$.subscribe(dropSource => {
			this.dropSource = dropSource;
		});
		this.sortableService.dragging$.subscribe(dragging => {
			this.sortable = dragging;
		});
	}

	@HostListener('mousedown', ['$event']) onMouseDown(event: any) {
		if (!this.handle || !this.handle.contains(event.target) || event.button !== 0) return;

		this.sortableService.dragging$.next(true);
		this.dragging = true;

		if (document.body.oncontextmenu)
			this.bodyFunc = document.body.oncontextmenu;

		document.body.oncontextmenu = (e) => { return false; };

		if (this.globalListenFunc)
			this.globalListenFunc();

		this.createGhost(event);

		this.globalListenFunc = this.renderer.listen('document', 'mousemove', e => {
			e.currentTarget.body.classList.add('dragging');
			this.moveGhost(e);
		});

		this.sortableService.dragSource$.next({ element: this.element, item: this.item });
	}

	@HostListener('document:mouseup', ['$event']) onMouseUp(event: any) {
		if (!this.dragging || event.button !== 0) return;
		this.dragging = false;
		if (this.globalListenFunc)
			this.globalListenFunc();
		event.currentTarget.body.classList.remove('dragging');
		this.removeGhost();
		this.sortableService.dragging$.next(false);
		this.sortableService.dragSource$.next(undefined);
		this.sortableService.dropSource$.next(undefined);

		document.body.oncontextmenu = this.bodyFunc ? this.bodyFunc : (e) => { };

		this.sortend.emit();
	}

	@HostListener('mousemove', ['$event']) onMouseMove(event: MouseEvent) {
		if (!this.dragSource || !this.dropSource) return;
		const dragElement = this.dragSource.element;
		const dropSourcePositions = this.dropSource!.getBoundingClientRect();
		const dragOffsetYOnDrop = event.clientY - dropSourcePositions.top;
		const threshold = dragOffsetYOnDrop <= 0 ? 0 : dragOffsetYOnDrop / dropSourcePositions.height;

		if (dragElement !== this.dropSource && this.dropSource && this.dropSource.parentNode) {
			const draggedItemIndex = this.items.findIndex(item => item == this.dragSource!.item);
			const draggedOverItemIndex = this.items.findIndex(item => item == this.item);
			if ((draggedItemIndex < draggedOverItemIndex && threshold >= 0.2) ||
				(draggedItemIndex > draggedOverItemIndex && threshold < 0.8)) {
				this.items.splice(draggedItemIndex, 0, this.items.splice(draggedOverItemIndex, 1)[0]);
				this.itemsChange.emit(this.items);
			}
		}
	}

	@HostListener('mouseover', ['$event']) onMouseOver(event: any) {
		if (!this.dragSource) return;
		const dragElement = this.dragSource.element;
		let dropTarget = event.target;
		while (dropTarget && dropTarget !== this.element && !dropTarget.hasAttribute('sortableItem')) {
			dropTarget = dropTarget.parentElement;
		}
		if (dropTarget.hasAttribute && dropTarget.hasAttribute('sortableItem') && dropTarget.parentElement == dragElement.parentElement) {
			this.sortableService.dropSource$.next(dropTarget);
			event.preventDefault();
			return;
		}
		return undefined;
	}

	private async createGhost(event: MouseEvent): Promise<void> {
		const rect = this.element.getBoundingClientRect();
		this.ghostOffsetX = event.clientX - rect.left;
		this.ghostOffsetY = event.clientY - rect.top;

		if (!this.dragging) return;

		const ghost = await toCanvas(this.element as HTMLElement, {
			width: rect.width,
			height: rect.height,
			pixelRatio: window.devicePixelRatio || 1,
			fontEmbedCSS: '',
		});
		ghost.classList.add('sortable-ghost');
		ghost.style.top = `${rect.top}px`;
		ghost.style.left = `${rect.left}px`;
		ghost.style.width = `${rect.width}px`;
		ghost.style.height = `${rect.height}px`;

		this.removeGhost();

		document.body.appendChild(ghost);

		this.ghostElement = ghost;
	}

	private moveGhost(event: MouseEvent): void {
		if (!this.ghostElement) return;
		this.ghostElement.style.opacity = '0.85';
		this.ghostElement.style.left = `${event.clientX - this.ghostOffsetX}px`;
		this.ghostElement.style.top = `${event.clientY - this.ghostOffsetY}px`;
	}

	private removeGhost(): void {
		if (this.ghostElement && document.body.contains(this.ghostElement))
			document.body.removeChild(this.ghostElement);
	}
}
